import { useCallback, useEffect, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';

/**
 * The password + mandatory-TOTP (AAL2) + membership gate, shared by every
 * operator-facing cockpit.
 *
 * EXTRACTED AT LADDER STEP 4 (2026-07-26). `apps/admin`, `apps/pms` and
 * `apps/reception` each carried a near-identical 250-line copy (F8). They
 * differed in exactly three things — the brand string, the membership check and
 * the refusal copy — so those are the parameters and nothing else is. A
 * security fix here now lands once instead of three times, which was the whole
 * point: three copies of an auth flow is three chances to patch two of them.
 *
 * WHY THE CLIENT IS INJECTED. `AuthGate` is the single sanctioned exception to
 * "a driver never names infrastructure" (ARCHITECTURE.md §2 L1), but a SHARED
 * gate cannot import any one app's `supabase` singleton. Taking the client as a
 * prop keeps the exception narrow: this component names the *type*, never a
 * concrete instance, and each app's composition root supplies its own.
 *
 * The flow, unchanged from the copies it replaces:
 *   no session            -> password login
 *   session, no factor    -> TOTP enrolment (2FA is MANDATORY, not offered)
 *   session, factor unmet -> TOTP challenge, once per session
 *   AAL2 reached          -> membership check -> app, or refusal
 */

/** The slice of supabase-js this gate uses. Structural, so any client with a
 *  matching `auth` surface satisfies it — including a fake in a test. */
export interface TotpAuthClient {
  auth: {
    getSession(): Promise<{ data: { session: unknown | null } }>;
    signInWithPassword(c: { email: string; password: string }): Promise<{ error: { message: string } | null }>;
    signOut(): Promise<unknown>;
    onAuthStateChange(cb: () => void): { data: { subscription: { unsubscribe(): void } } };
    mfa: {
      getAuthenticatorAssuranceLevel(): Promise<{
        data: { currentLevel: string | null; nextLevel: string | null } | null;
      }>;
      listFactors(): Promise<{
        data: { totp?: { id: string }[]; all?: { id: string; status: string }[] } | null;
      }>;
      enroll(o: { factorType: 'totp' }): Promise<{
        data: { id: string; totp: { qr_code: string; secret: string } } | null;
        error: { message: string } | null;
      }>;
      challengeAndVerify(o: { factorId: string; code: string }): Promise<{ error: { message: string } | null }>;
      unenroll(o: { factorId: string }): Promise<unknown>;
    };
  };
}

export interface TotpAuthGateProps {
  /** Supplied by the app's composition root — never imported here. */
  client: TotpAuthClient;
  /** Brand line shown on the card and while booting, e.g. « Réception Le Montaigu ». */
  brand: string;
  /** Membership predicate. Each cockpit gates on a different role — is_admin()
   *  for bos.*, a `staff` self-select for the desk and the PMS. Runs only once
   *  AAL2 is reached. Must not throw: return false on error. */
  checkMembership: () => Promise<boolean>;
  /** App-specific refusal copy (FR, staff-facing). */
  deniedMessage: string;
  children: ReactNode;
}

type Stage = 'loading' | 'login' | 'enroll' | 'challenge' | 'denied' | 'ready';

export function TotpAuthGate({
  client,
  brand,
  checkMembership,
  deniedMessage,
  children,
}: TotpAuthGateProps) {
  const [stage, setStage] = useState<Stage>('loading');

  const evaluate = useCallback(async () => {
    const {
      data: { session },
    } = await client.auth.getSession();
    if (!session) {
      setStage('login');
      return;
    }
    const { data: aal } = await client.auth.mfa.getAuthenticatorAssuranceLevel();
    if (aal?.currentLevel === 'aal2') {
      setStage((await checkMembership()) ? 'ready' : 'denied');
    } else if (aal?.nextLevel === 'aal2') {
      setStage('challenge'); // a verified factor exists — must pass it this session
    } else {
      setStage('enroll'); // no factor yet — must enrol
    }
  }, [client, checkMembership]);

  useEffect(() => {
    void evaluate();
    const { data: sub } = client.auth.onAuthStateChange(() => {
      void evaluate();
    });
    return () => sub.subscription.unsubscribe();
  }, [client, evaluate]);

  if (stage === 'loading') return <div className="boot">{brand} — chargement…</div>;
  if (stage === 'login') return <Login client={client} brand={brand} onDone={evaluate} />;
  if (stage === 'enroll') return <MfaEnroll client={client} brand={brand} onDone={evaluate} />;
  if (stage === 'challenge') return <MfaChallenge client={client} brand={brand} onDone={evaluate} />;
  if (stage === 'denied') return <NotAuthorized client={client} brand={brand} message={deniedMessage} />;
  return <>{children}</>;
}

function Shell({ brand, title, children }: { brand: string; title: string; children: ReactNode }) {
  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <div className="auth-brand">{brand}</div>
        <h1 className="auth-title">{title}</h1>
        {children}
      </div>
    </div>
  );
}

function Login({ client, brand, onDone }: { client: TotpAuthClient; brand: string; onDone: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr('');
    const { error } = await client.auth.signInWithPassword({ email: email.trim(), password });
    setBusy(false);
    if (error) setErr(error.message);
    else onDone();
  };

  return (
    <Shell brand={brand} title="Connexion">
      <form onSubmit={submit} className="auth-form">
        <label className="auth-field">
          <span>Adresse e-mail</span>
          <input
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </label>
        <label className="auth-field">
          <span>Mot de passe</span>
          <input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </label>
        {err && <p className="auth-err">{err}</p>}
        <button className="auth-btn" type="submit" disabled={busy}>
          {busy ? '…' : 'Se connecter'}
        </button>
      </form>
    </Shell>
  );
}

function MfaChallenge({ client, brand, onDone }: { client: TotpAuthClient; brand: string; onDone: () => void }) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr('');
    const { data: factors } = await client.auth.mfa.listFactors();
    const totp = factors?.totp?.[0];
    if (!totp) {
      setBusy(false);
      setErr('Aucun authentificateur enregistré.');
      return;
    }
    const { error } = await client.auth.mfa.challengeAndVerify({ factorId: totp.id, code: code.trim() });
    setBusy(false);
    if (error) setErr(error.message);
    else onDone();
  };

  return (
    <Shell brand={brand} title="Vérification en deux étapes">
      <form onSubmit={submit} className="auth-form">
        <p className="auth-hint">Saisissez le code à 6 chiffres de votre application d’authentification.</p>
        <label className="auth-field">
          <span>Code</span>
          <input
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            maxLength={6}
            required
          />
        </label>
        {err && <p className="auth-err">{err}</p>}
        <button className="auth-btn" type="submit" disabled={busy}>
          {busy ? '…' : 'Vérifier'}
        </button>
        <button type="button" className="auth-link" onClick={() => void client.auth.signOut()}>
          Se déconnecter
        </button>
      </form>
    </Shell>
  );
}

function MfaEnroll({ client, brand, onDone }: { client: TotpAuthClient; brand: string; onDone: () => void }) {
  const [qr, setQr] = useState('');
  const [secret, setSecret] = useState('');
  const [factorId, setFactorId] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => {
      // Clear any stale unverified factor so enroll can't collide.
      const list = await client.auth.mfa.listFactors();
      for (const f of list.data?.all ?? []) {
        if (f.status === 'unverified') await client.auth.mfa.unenroll({ factorId: f.id });
      }
      const { data, error } = await client.auth.mfa.enroll({ factorType: 'totp' });
      if (error || !data) {
        setErr(error?.message ?? 'Enrôlement impossible.');
        return;
      }
      setFactorId(data.id);
      setQr(data.totp.qr_code);
      setSecret(data.totp.secret);
    })();
  }, [client]);

  const verify = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr('');
    const { error } = await client.auth.mfa.challengeAndVerify({ factorId, code: code.trim() });
    setBusy(false);
    if (error) setErr(error.message);
    else onDone();
  };

  return (
    <Shell brand={brand} title="Activer la double authentification">
      <p className="auth-hint">
        Scannez ce QR code avec Google Authenticator (ou équivalent), puis saisissez le code généré.
      </p>
      {qr ? <img className="auth-qr" src={qr} alt="QR code" /> : <p className="auth-hint">Génération…</p>}
      {secret && (
        <p className="auth-secret">
          Clé manuelle : <code>{secret}</code>
        </p>
      )}
      <form onSubmit={verify} className="auth-form">
        <label className="auth-field">
          <span>Code à 6 chiffres</span>
          <input
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            maxLength={6}
            required
          />
        </label>
        {err && <p className="auth-err">{err}</p>}
        <button className="auth-btn" type="submit" disabled={busy || !factorId}>
          {busy ? '…' : 'Activer'}
        </button>
        <button type="button" className="auth-link" onClick={() => void client.auth.signOut()}>
          Se déconnecter
        </button>
      </form>
    </Shell>
  );
}

function NotAuthorized({ client, brand, message }: { client: TotpAuthClient; brand: string; message: string }) {
  return (
    <Shell brand={brand} title="Accès non autorisé">
      <p className="auth-hint">{message}</p>
      <button type="button" className="auth-btn" onClick={() => void client.auth.signOut()}>
        Se déconnecter
      </button>
    </Shell>
  );
}
